import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, Plus, Search, LifeBuoy } from "lucide-react";
import { api, errorToMessage } from "@/lib/apiClient";
import { canCasesAuthor, useAuth } from "@/lib/auth";
import { CASES } from "@/constants/testIds";

const STATUS_COLORS = {
  OPEN: "bg-slate-100 text-slate-700",
  ASSIGNED: "bg-sky-100 text-sky-800",
  INSPECTION: "bg-indigo-100 text-indigo-800",
  AWAITING_CUSTOMER: "bg-amber-100 text-amber-800",
  APPROVED: "bg-teal-100 text-teal-800",
  REJECTED: "bg-rose-100 text-rose-800",
  REPAIR: "bg-violet-100 text-violet-800",
  REPLACEMENT: "bg-fuchsia-100 text-fuchsia-800",
  CLOSED: "bg-emerald-100 text-emerald-800",
};
const PRIORITY_COLORS = {
  LOW: "bg-slate-100 text-slate-600",
  MEDIUM: "bg-sky-100 text-sky-700",
  HIGH: "bg-amber-100 text-amber-800",
  URGENT: "bg-rose-100 text-rose-800",
};
const WARRANTY_COLORS = {
  IN_WARRANTY: "text-emerald-700",
  BATTERY_COVERED: "text-teal-700",
  OUT_OF_WARRANTY: "text-slate-500",
  PHYSICAL_DAMAGE: "text-rose-700",
  ACCIDENTAL_DAMAGE: "text-orange-700",
  UNKNOWN: "text-slate-400",
};

function Chip({ text, cls }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls || "bg-slate-100 text-slate-700"}`}>
      {text}
    </span>
  );
}

export default function CasesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, total_pages: 1 });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [caseType, setCaseType] = useState("");
  const [priority, setPriority] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const params = useMemo(() => ({
    page, page_size: pageSize,
    q: q || undefined,
    status: status || undefined,
    case_type: caseType || undefined,
    priority: priority || undefined,
  }), [page, pageSize, q, status, caseType, priority]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/cases", { params });
      setRows(data.items);
      setMeta(data.meta);
    } catch (e) {
      setError(errorToMessage(e, "Failed to load cases"));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="cases-page">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Customer Cases</h1>
          <p className="text-sm text-slate-500 mt-1">
            Complaints, returns, replacements, warranty claims and repair requests. Search by any identifier.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={load}
            className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
            <RefreshCw size={14} /> Refresh
          </button>
          {canCasesAuthor(user) && (
            <button type="button" data-testid={CASES.newBtn}
              onClick={() => navigate("/admin/cases/new")}
              className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800">
              <Plus size={14} /> New Case
            </button>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4 grid grid-cols-1 sm:grid-cols-5 gap-3 shadow-sm">
        <div className="sm:col-span-2 relative">
          <Search size={14} className="absolute left-3 top-2.5 text-slate-400" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
            data-testid={CASES.searchInput}
            placeholder="Case No · Customer · Mobile · Invoice · Barcode · Serial"
            className="w-full rounded-md border border-slate-200 pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800" />
        </div>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          data-testid={CASES.statusFilter}
          className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800">
          <option value="">All statuses</option>
          {["OPEN","ASSIGNED","INSPECTION","AWAITING_CUSTOMER","APPROVED","REJECTED","REPAIR","REPLACEMENT","CLOSED"].map((s) => (
            <option key={s} value={s}>{s.replace("_", " ")}</option>
          ))}
        </select>
        <select value={caseType} onChange={(e) => { setCaseType(e.target.value); setPage(1); }}
          data-testid={CASES.typeFilter}
          className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800">
          <option value="">All types</option>
          {["COMPLAINT","RETURN","REPLACEMENT","WARRANTY_CLAIM","REPAIR_REQUEST"].map((t) => (
            <option key={t} value={t}>{t.replace("_", " ")}</option>
          ))}
        </select>
        <select value={priority} onChange={(e) => { setPriority(e.target.value); setPage(1); }}
          data-testid={CASES.priorityFilter}
          className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-slate-800">
          <option value="">All priorities</option>
          {["LOW","MEDIUM","HIGH","URGENT"].map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
        <table className="w-full text-sm" data-testid={CASES.listTable}>
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Case No.</th>
              <th className="text-left px-4 py-3">Created</th>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-left px-4 py-3">Customer</th>
              <th className="text-left px-4 py-3">Passport</th>
              <th className="text-left px-4 py-3">Warranty</th>
              <th className="text-left px-4 py-3">Priority</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Assigned</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-16 text-center text-slate-400">
                <LifeBuoy size={24} className="inline-block mb-2 text-slate-300" />
                <div>No cases match your filters.</div>
              </td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.id} data-testid={CASES.listRow(r.id)}
                className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                onClick={() => navigate(`/admin/cases/${r.id}`)}>
                <td className="px-4 py-3 font-mono text-xs text-slate-900">{r.case_no}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3 text-xs text-slate-700">{r.case_type.replace("_", " ")}</td>
                <td className="px-4 py-3">
                  <div className="text-slate-900 font-medium">{r.customer_name || "—"}</div>
                  <div className="text-xs text-slate-500">{r.mobile || ""}</div>
                </td>
                <td className="px-4 py-3 text-xs">
                  <div className="font-mono text-slate-800">{r.passport_barcode || "—"}</div>
                  <div className="font-mono text-slate-500">{r.passport_serial || ""}</div>
                </td>
                <td className={`px-4 py-3 text-xs ${WARRANTY_COLORS[r.warranty_status] || ""}`}>
                  {r.warranty_status ? r.warranty_status.replace("_", " ") : "—"}
                </td>
                <td className="px-4 py-3"><Chip text={r.priority} cls={PRIORITY_COLORS[r.priority]} /></td>
                <td className="px-4 py-3"><Chip text={r.status.replace("_", " ")} cls={STATUS_COLORS[r.status]} /></td>
                <td className="px-4 py-3 text-xs text-slate-600">{r.assigned_to_email || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex items-center justify-between px-4 py-3 text-xs text-slate-500 border-t border-slate-100">
          <span>{meta.total} case{meta.total === 1 ? "" : "s"}</span>
          <div className="flex items-center gap-2">
            <button data-testid={CASES.pagePrev} disabled={page <= 1}
              onClick={() => setPage(page - 1)}
              className="rounded-md border border-slate-300 bg-white px-2 py-1 disabled:opacity-40">Prev</button>
            <span>Page {page} of {meta.total_pages || 1}</span>
            <button data-testid={CASES.pageNext} disabled={page >= (meta.total_pages || 1)}
              onClick={() => setPage(page + 1)}
              className="rounded-md border border-slate-300 bg-white px-2 py-1 disabled:opacity-40">Next</button>
          </div>
        </div>
      </div>
    </div>
  );
}
